import React, { useState, useRef, useEffect } from 'react';
import { Search } from './Icons.jsx';

const PHOTON_URL = 'https://photon.komoot.io/api/';
const DEBOUNCE_MS = 300;

/**
 * Formatiert eine Adresse aus Photon-Feature-Properties (für Anzeige in der Liste).
 */
function formatAddress(properties) {
  if (!properties) return '';
  const { name, street, housenumber, postcode, city, country } = properties;
  const parts = [];
  if (street || name) {
    const str = [street, housenumber].filter(Boolean).join(' ') || name;
    if (str) parts.push(str);
  }
  if (postcode || city) {
    parts.push([postcode, city].filter(Boolean).join(' '));
  }
  if (country) parts.push(country);
  return parts.filter(Boolean).join(', ') || name || '';
}

/**
 * Liefert strukturierte Adressfelder aus Photon-Properties.
 * @returns {{ street: string, zip: string, city: string, address2: string }}
 */
export function getStructuredAddress(properties) {
  if (!properties) return { street: '', zip: '', city: '', address2: '' };
  const { street = '', housenumber = '', postcode = '', city = '', name = '' } = properties;
  const streetLine = [street, housenumber].filter(Boolean).join(' ') || name || '';
  return {
    street: streetLine,
    zip: postcode || '',
    city: city || '',
    address2: '',
  };
}

/**
 * Adress-Suche mit Photon (OpenStreetMap/Komoot) – dauerhaft kostenlos, kein API-Key.
 * onSelect({ street, zip, city, address2 }) wird mit den strukturierten Feldern aufgerufen.
 */
export function AddressAutocomplete({
  onSelect,
  countryCode = 'de',
  placeholder = 'Adresse suchen (kostenlos, OpenStreetMap)…',
  className = '',
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      const url = new URL(PHOTON_URL);
      url.searchParams.set('q', query.trim());
      url.searchParams.set('limit', '8');
      url.searchParams.set('lang', 'de');
      fetch(url.toString())
        .then((res) => res.json())
        .then((data) => {
          const list = (data.features || []).map((f) => ({
            id: f.properties?.osm_id || f.place_id || Math.random(),
            formatted: formatAddress(f.properties),
            properties: f.properties,
          })).filter((a) => a.formatted);
          setSuggestions(list);
          setOpen(list.length > 0);
        })
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
      debounceRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    setQuery(item.formatted);
    setOpen(false);
    setSuggestions([]);
    if (onSelect) {
      const structured = getStructuredAddress(item.properties);
      onSelect(structured);
    }
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
          <Search size={18} />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          autoComplete="off"
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 text-sm text-slate-800"
                onClick={() => handleSelect(item)}
              >
                {item.formatted}
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && <p className="text-xs text-slate-500 mt-1">Suche…</p>}
      {!loading && query && suggestions.length === 0 && (
        <p className="text-xs text-slate-500 mt-1">Adresse tippen, dann Vorschlag auswählen – Anschrift wird übernommen.</p>
      )}
    </div>
  );
}
